import _ from 'lodash';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { SnackBarService } from 'src/app/shared/services/snackbar.service';
import { InjectorModule } from 'src/app/shared/modules/injector.module';
import { Router } from '@angular/router';
import { AppConfigService } from './app-config.service';

export type SubscriptionPlanErrorHandler = (errorResponse: HttpErrorResponse) => boolean;

export type ErrorHandler = (
  errorResponse: HttpErrorResponse,
  ok404?: boolean,
  rethrowError?: boolean
) => Observable<HttpErrorResponse | null>;

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerFactory {
  private snackbarService: SnackBarService;
  private appConfigService: AppConfigService;
  private router: Router;
  private rootPath: string;
  constructor() {
    this.snackbarService = InjectorModule.injector.get(SnackBarService);
    this.appConfigService = InjectorModule.injector.get(AppConfigService);
    this.router = InjectorModule.injector.get(Router);
    this.rootPath = this.appConfigService.config?.baseRoute || '';
  }

  public buildHandler = (
    on401?: () => boolean,
    onSubscriptionPlanError?: SubscriptionPlanErrorHandler,
    onErrorCallBack?: () => void
  ): ErrorHandler => {
    return (errorResponse: HttpErrorResponse, ok404: boolean, rethrowError: boolean) => {
      onErrorCallBack?.();

      if (errorResponse.status == 0 && errorResponse.statusText == 'Unknown Error') {
        this.appConfigService.loadError = true;
        this.router.navigate([`${this.rootPath}/maintenance-mode`], { skipLocationChange: true });
        return of(null);
      }
      let showSnack = true;
      let snackError: string;

      if (errorResponse.status == 401) {
        const isHandled = on401?.();
        if (isHandled) {
          return of(null);
        }

        snackError = `You don't have permission to do that.`;
      } else if (errorResponse.status == 404) {
        showSnack = false;
        if (!ok404) {
          this.router.navigate([`${this.rootPath}/not-found`], { skipLocationChange: true });
        }
      } else if (errorResponse.status == 400 && rethrowError) {
        showSnack = false;
      }

      if (onSubscriptionPlanError?.(errorResponse)) {
        //handled the error and snack
        showSnack = false;
      }

      if (showSnack) {
        const message = snackError || getParsedError(errorResponse).message;
        if (errorResponse.status == 403) {
          //403 where showSnack is true, we'll goto route and not show the message
          this.router.navigate([`${this.rootPath}/permission-denied`], {
            skipLocationChange: true,
            state: { message: message }
          });
        } else {
          this.snackbarService.open(message, false);
        }
      }

      return rethrowError ? throwError(() => errorResponse) : of(null);
    };
  };
}

const replaceEndPunctuation = (strValue: string) => {
  if (strValue.trim().slice(-1) === '.') {
    strValue = strValue.trim().slice(0, -1).concat('; ');
  } else {
    strValue = strValue.concat('; ');
  }
  return strValue;
};
export const genericErrorMessage = `An error occurred while submitting your request.
                                Please try again later or contact customer support if this issue persists.`;
const isGenericError = {
  isGenericError: true,
  message: genericErrorMessage
};

/** Handles parsing an httpError. Should have a 'error' and 'status' property.
 * Error can be a dictionary, string, or array
 * For dictionary, the values should be string or string[].
 */
export const getParsedError = (errorResponse): { isGenericError: boolean; message: string } => {
  const body = errorResponse.error;
  if (!body || errorResponse.status < 400 || errorResponse.status >= 500) {
    return isGenericError;
  }
  let numberOfErrors: number = 0;
  let preMultipleErrorText: string = 'Multiple errors detected: ';
  let thisError: string = '';

  if (typeof body === 'string') {
    // Single error
    numberOfErrors = 1;
    thisError = replaceEndPunctuation(thisError.concat(body));
  } else if (body.length) {
    // Array of errors
    body.forEach((error) => {
      numberOfErrors++;
      thisError = replaceEndPunctuation(thisError.concat(error));
    });
  } else {
    // ModelState (object) errors
    _.forIn(body, function (value, key) {
      if (typeof value === 'string') {
        numberOfErrors++;
        thisError = replaceEndPunctuation(thisError.concat(value));
      } else if (value.length && typeof value[0] === 'string') {
        numberOfErrors++;
        //assume is array of strings
        thisError = replaceEndPunctuation(thisError.concat(value));
      } else {
        console.error(`Unable to parse error: ${JSON.stringify(value)}`);
      }
    });
  }

  if (numberOfErrors > 1) {
    thisError = preMultipleErrorText.concat(thisError);
  }

  if (thisError) {
    const message = thisError.slice(0, -2).concat('.');
    return { isGenericError: false, message };
  }
  return isGenericError;
};

export const getErrorMessage = (errorResponse, fallbackMessage: string): string => {
  const { isGenericError, message } = getParsedError(errorResponse);
  return isGenericError ? fallbackMessage : message;
};
